'use client';

import type React from 'react';

import { urlFor } from '@skybeam/shared-sanity';
import { cn } from '@skybeam/shared-utils/classnames/cn';
import { ExternalVideo } from '../../../_components/external-video';
import { utils } from '../../../_components/utils';
import type {
  ResponsivePadding,
  ResponsiveValue,
  SanityImage,
} from '../../../_components/utils/types';

const DEFAULT_BG_IMAGE_URL = '/bg-beams-dark.png';

export type VideoWithStepsSectionProps = {
  backgroundColor?: string;
  backgroundImage?: SanityImage;
  containerPadding?: ResponsivePadding;
  contentMaxWidth?: ResponsiveValue;
  heading: string;
  headingHighlight?: string;
  headingHighlightColor?: string;
  steps: Array<{ label: string; number: number }>;
  videoAspect?: string;
  videoUrl: string;
};

export const VideoWithStepsSection = ({
  backgroundColor = '#0A0A0A',
  backgroundImage,
  containerPadding,
  contentMaxWidth,
  heading,
  headingHighlight,
  headingHighlightColor,
  steps,
  videoAspect,
  videoUrl,
}: VideoWithStepsSectionProps) => {
  const containerPaddingStyle =
    utils.responsiveUtils.getResponsivePaddingStyleTag(containerPadding);
  const contentMaxWidthStyle = utils.responsiveUtils.getResponsiveValueStyleTag(
    contentMaxWidth,
    'max-width',
  );
  const styleTags = [
    containerPaddingStyle.styleTag,
    contentMaxWidthStyle.styleTag,
  ].filter((tag): tag is React.ReactElement => tag !== null);
  const bgImageUrl =
    backgroundImage?.asset != null
      ? urlFor(backgroundImage.asset).width(1920).url()
      : DEFAULT_BG_IMAGE_URL;
  const aspectRatio = videoAspect?.trim() || undefined;

  return (
    <>
      {styleTags}
      <div
        data-section="product-run-local-campaigns-section"
        className={cn(
          'tablet:py-16 laptop:py-20 desktop:py-24 flex w-full justify-center bg-cover bg-center bg-no-repeat px-6 py-12',
          containerPaddingStyle.className,
        )}
        style={{
          backgroundColor,
          backgroundImage: `url(${bgImageUrl})`,
        }}
      >
        <div
          className={cn(
            'flex w-full flex-col gap-y-10',
            contentMaxWidthStyle.className,
          )}
        >
          <h2 className="heading-marketing-2 laptop:px-[93px] desktop:px-[38px] text-center font-light text-white">
            {heading}
            {headingHighlight && (
              <span
                {...utils.highlightSpanProps.getHighlightSpanProps(
                  headingHighlightColor,
                )}
              >
                {' '}
                {headingHighlight}
              </span>
            )}
          </h2>
          <div
            className="tablet:p-3 laptop:p-4 w-full rounded-lg bg-white p-1.5"
            style={aspectRatio ? { aspectRatio } : undefined}
          >
            <ExternalVideo
              className="relative size-full overflow-hidden rounded-md"
              url={videoUrl}
            />
          </div>
          <div className="laptop:flex-row laptop:justify-center laptop:gap-x-16 flex flex-col items-center gap-y-6 text-white">
            {steps.map((step) => (
              <div key={step.number} className="flex gap-x-1.5">
                <div className="flex size-6 items-center justify-center rounded-full bg-blue-700 text-base text-black">
                  {step.number}
                </div>
                <div className="tablet:text-lg laptop:text-xl text-base">
                  {step.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};
